import { Outlet, matchPath, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { container } from "tsyringe";
import ModuleConfig from "configs/ModuleConfig";
import SubscriptionService from "services/SubscriptionService";
import { TCorporationProductName, getCorporationProductsAggregate } from "common/resources/Scpi/Corporation/Products";
import { EPatrimoineRevenusCorporationGroups } from "common/enums/Scpi/Corporation/Groups/EPatrimoineRevenusCorporationGroups";
import { EConnaissanceExperienceCorporationGroups } from "common/enums/Scpi/Corporation/Groups/EConnaissanceExperienceCorporationGroups";
import { ETestAdequationCorporationGroups } from "common/enums/Scpi/Corporation/Groups/ETestAdequationCorporationGroups";
import { EAutreCgpCorporationGroups } from "common/enums/Scpi/Corporation/Groups/EAutreCgpCorporationGroups";
import { EFinanceDurableCorporationGroups } from "common/enums/Scpi/Corporation/Groups/EFinanceDurableCorporationGroups";
import GenericPatrimoineRequestResourceCorporation from "common/resources/Scpi/Corporation/GenericPatrimoineRequestResource";
import GenericConnaissanceExperienceRequestResourceCorporation from "common/resources/Scpi/Corporation/GenericConnaissanceExperienceRequestResource";
import GenericTestAdequationRequestResourceCorporation from "common/resources/Scpi/Corporation/GenericTestAdequationRequestResource";
import GenericFinanceDurableRequestResourceCorporation from "common/resources/Scpi/Corporation/GenericFinanceDurableRequestResource";
import GenericAutreCgpRequestResourceCorporation from "common/resources/Scpi/Corporation/GenericAutreCgpRequestResource";
import I18n from "components/materials/I18n";
import InvestorPortalOrigin from "components/materials/InvestorPortalOrigin";
import SubscriptionResponseResource from "common/resources/Subscription/SubscriptionResponseResource";
import * as P from "ts-pattern";
import Modal from "components/elements/Modal";
import Typography, { ITypo } from "components/elements/Typography";
import Button, { EButtonVariant } from "components/elements/Button";
import classes from "./classes.module.scss";
import useOpenable from "hooks/useOpenable";
import _ from "lodash";
import NumberUtils from "utils/NumberUtils";
import { GenericCorporationProduct } from "common/resources/Scpi/Corporation/GenericCorporationProduct";
import { ESubFilesTypes } from "common/enums/Subscription/File/ESubFilesTypes";
import { isPrivateEquityOnly, PERCEPTION_RIB_FILES } from "common/utils/privateEquity";
import CorporationSheetLightResponseResource from "common/resources/CorporationSheet/CorporationSheetLightResponseResource";
import SubscriptionQuestionsStepper from "./Stepper";
import ValidationDocumentsStepperCorporation, { IDocumentValidationSteps } from "./ValidationDocumentsStepperCorporation";
import { EDocumentValidationStatus } from "common/enums/Subscription/EDocumentValidationStatus";
import SubscriptionLightUpdateRequestResource from "common/resources/Subscription/SubscriptionLightUpdateRequestResource";
import EditCorporationModal from "../EditCorporationModal";
import CorporationSheetResponseResource from "common/resources/CorporationSheet/CorporationSheetResponseResource";
import CorporationService from "services/CorporationService";
import { isProductEligibleForElectronicSignature } from "utils/signatureUtils";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import { getViagerRatio } from "utils/baremeViager";

export type ISubscriptionSteps = {
	category: keyof GenericCorporationProduct;
	path: string;
	title: string;
};

export type ISubscriptionOutletContext = {
	productAggregate: GenericCorporationProduct;
	getInputsFromProductAggregate: (
		inputList:
			| Record<EPatrimoineRevenusCorporationGroups, JSX.Element | null>
			| Record<EConnaissanceExperienceCorporationGroups, JSX.Element | null>
			| Record<ETestAdequationCorporationGroups, JSX.Element | null>
			| Record<EAutreCgpCorporationGroups, JSX.Element | null>
			| Record<EFinanceDurableCorporationGroups, JSX.Element | null>,
	) => JSX.Element[];
	updateSubscription: (
		resource:
			| GenericPatrimoineRequestResourceCorporation
			| GenericConnaissanceExperienceRequestResourceCorporation
			| GenericTestAdequationRequestResourceCorporation
			| GenericFinanceDurableRequestResourceCorporation
			| GenericAutreCgpRequestResourceCorporation,
	) => void;
	updateConditionsSubscription: (resource: SubscriptionLightUpdateRequestResource) => void;
	subscription: SubscriptionResponseResource;
	goToNextStep: () => void;
	canSendSubscription: boolean;
	loadSubscription: () => void;
	sendSubcriptionToMo: () => void;
	setSubscriptionHasUnsavedChangesToTrue: (e: React.ChangeEvent<HTMLFormElement>) => void;
	setSubscriptionHasUnsavedChangesToFalse: () => void;
	currentSelectedPath: string;
	setCurrentSelectedPath: (path: string) => void;
	subscriptionWithKnowledge: SubscriptionResponseResource;
	setCanEnableDocumentStep: (value: boolean) => void;
	canEnableDocumentStep: boolean;
	documentValidationSteps: IDocumentValidationSteps[];
};

const corporationService = container.resolve(CorporationService);
const subscriptionService = container.resolve(SubscriptionService);
const modules = container.resolve(ModuleConfig).get().modules;

function getProductSubscribedAmount(productSubscribed: NonNullable<SubscriptionResponseResource["productsSubscribed"]>[number]): number {
	const { subscriptionProductType, demembrementDuration, demembrementType, viagerAge, quantity, productSheet } = productSubscribed;
	const basePrice = quantity * productSheet!.price;
	if (!subscriptionProductType || subscriptionProductType === ESubscriptionProductType.pleine_propriete) return basePrice;
	if (demembrementType === EDemembrementType.viager && viagerAge != null) {
		return basePrice * (getViagerRatio(viagerAge, subscriptionProductType) ?? 1);
	}
	if (demembrementDuration) {
		const key = productSheet?.demembrementKeys?.find((k) => k.duration === demembrementDuration);
		if (key) return basePrice * (subscriptionProductType === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio);
	}
	return basePrice;
}

const subscriptionCorporationPages = modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages;
const stepByCategory: ISubscriptionSteps[] = [
	{
		category: "payment_methods",
		path: subscriptionCorporationPages.PaymentMethods.props.path,
		title: "Règlement",
	},
	{
		category: "patrimoine_revenus",
		path: subscriptionCorporationPages.PatrimoineRevenus.props.path,
		title: "Patrimoine & revenus",
	},
	{
		category: "connaissance_experience",
		path: subscriptionCorporationPages.ConnaissanceExperience.props.path,
		title: "Connaissance & expérience représentant légal",
	},
	{
		category: "finance_durable",
		path: subscriptionCorporationPages.FinanceDurable.props.path,
		title: "Finance durable",
	},
	{
		category: "test_adequation",
		path: subscriptionCorporationPages.TestAdequation.props.path,
		title: "Test d'adéquation",
	},
	{
		category: "autre_cgp",
		path: subscriptionCorporationPages.AutreCgp.props.path,
		title: "Appréciation sur l'opération",
	},
	{
		category: "files",
		path: subscriptionCorporationPages.Files.props.path,
		title: "Dépôt des documents",
	},
];

export default function SubscriptionQuestionsCorporation() {
	const editCorporationModal = useOpenable();
	const { subscriptionId } = useParams();
	const [productAggregate, setProductAggregate] = useState<GenericCorporationProduct | null>(null);
	const [actualCategory, setActualCategory] = useState<keyof GenericCorporationProduct | null>(null);
	const [subscription, setSubscription] = useState<SubscriptionResponseResource | null>(null);
	const [subscriptionWithKnowledge, setSubscriptionWithKnowledge] = useState<SubscriptionResponseResource | null>(null);
	const [canSendSubscription, setCanSendSubscription] = useState(false);
	const [subscriptionHasUnsavedChanges, setSubscriptionHasUnsavedChanges] = useState<boolean>(false);
	const [currentSelectedPath, setCurrentSelectedPath] = useState<string>("");
	const [currentFormElement, setCurrentFormElement] = useState<HTMLFormElement | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasTutoModalBeenOpened, setHasTutoModalBeenOpened] = useState(false);
	const [canEnableDocumentStep, setCanEnableDocumentStep] = useState(false);
	const [shouldTriggerDocumentResetWarning, setShouldTriggerDocumentResetWarning] = useState(false);
	const { isOpen: isOpenResetWarningModal, open: openResetWarningModal, close: closeResetWarningModal } = useOpenable();
	const [pendingSubmitCallback, setPendingSubmitCallback] = useState<(() => void) | null>(null);
	const [validateDocumentsStepEnabled, setValidateDocumentsStepEnabled] = useState(false);
	const [corporation, setCorporation] = useState<CorporationSheetResponseResource | null>(null);
	const subscriber = subscription?.productsSubscribed?.[0]?.corporationSubscriber;

	const filteredSteps = useMemo(
		() =>
			stepByCategory
				.filter((step) => !!productAggregate?.[step.category]?.groups.length)
				.map((step) => ({ ...step, path: step.path.replace(":subscriptionId", subscriptionId ?? "") })),
		[productAggregate, subscriptionId],
	);

	const requiresElectronicSignature = useMemo(() => {
		return subscription?.productsSubscribed?.some(isProductEligibleForElectronicSignature) ?? false;
	}, [subscription]);

	const { isOpen: isOpenUnsavedChangeModal, close: closeUnsavedChangeModal, open: openUnsavedChangeModal } = useOpenable();
	const { isOpen: isOpenValidatedModal, close: closeValidatedModal, open: openValidatedModal } = useOpenable();
	const { isOpen: isOpenSignDocumentTutoModal, close: closeSignDocumentTutoModal, open: openSignDocumentTutoModal } = useOpenable();

	const navigate = useNavigate();
	const location = useLocation();

	const checkCategoryIsCompleted = useCallback(
		(category: keyof GenericCorporationProduct) => {
			if (!subscription) return false;
			return P.match(category)
				.with("autre_cgp", () => {
					return !!subscription.autreCGP;
				})
				.with("patrimoine_revenus", () => {
					return !!subscription.patrimoineRevenus;
				})
				.with("connaissance_experience", () => {
					return !!subscription.connaissanceExperience;
				})
				.with("test_adequation", () => {
					return !!subscription.testAdequation;
				})
				.with("finance_durable", () => {
					return !!subscription.financeDurable;
				})
				.with("payment_methods", () => {
					return !!(
						subscription.fundsOriginCorporation &&
						subscription.financialObjective &&
						subscription.fundsOriginCorporation.length >= 1 &&
						subscription.financialObjective.length >= 1
					);
				})
				.with("files", () => {
					if (!productAggregate) return false;
					const productSubscribedHasFiles = subscription.productsSubscribed?.every((product) => !!product.productSubscribedToFiles) ?? false;
					if (!productSubscribedHasFiles) return false;
					let hasAllFiles = true;
					const withoutPerceptionRib = isPrivateEquityOnly(subscription.productsSubscribed);
					productAggregate.files.groups.forEach((file) => {
						if (withoutPerceptionRib && PERCEPTION_RIB_FILES.includes(file)) return;
						if (file === ESubFilesTypes.OTHERS) return;
						if (file === ESubFilesTypes.DERNIER_RELEVE_COMPTE) return;
						if (file === ESubFilesTypes.LIASSE_FISCALE) return;
						if (file === ESubFilesTypes.RIB_PRELEVEMENT_CORPORATION) return;
						subscription.productsSubscribed?.forEach((product) => {
							if (!product.productSubscribedToFiles?.find((subFile) => subFile.type === file)) hasAllFiles = false;
						});
					});

					if (
						hasBeneficiaries(subscription.productsSubscribed?.[0]?.corporationSubscriber) &&
						!subscription.productsSubscribed?.find((product) => product.productSubscribedToFiles?.find((subFile) => subFile.type === ESubFilesTypes.BENEFICIARIES_ID))
					) {
						hasAllFiles = false;
					}

					if (
						hasBeneficiaries(subscription.productsSubscribed?.[0]?.corporationSubscriber) &&
						!subscription.productsSubscribed?.find((product) =>
							product.productSubscribedToFiles?.find((subFile) => subFile.type === ESubFilesTypes.BENEFICIARIES_JUSTIFICATIF_DOMICILE),
						)
					) {
						hasAllFiles = false;
					}

					return hasAllFiles;
				})
				.exhaustive();
		},
		[productAggregate, subscription],
	);

	function hasBeneficiaries(corporation: CorporationSheetLightResponseResource | null | undefined) {
		if (!corporation?.representantLegal) return false;
		const corporationBeneficiaries = corporation.beneficiaries.filter((beneficiary) => beneficiary.id !== corporation.representantLegal!.id);
		return !(corporationBeneficiaries.length === 0);
	}

	// Pas de convention RTO en private equity : la validation commence par les documents du produit.
	const privateEquityOnly = useMemo(() => isPrivateEquityOnly(subscription?.productsSubscribed), [subscription]);

	const documentValidationSteps: IDocumentValidationSteps[] = useMemo(() => {
		return [
			...(privateEquityOnly
				? []
				: [
						{
							status: subscription?.rtoDocumentsStatus ?? EDocumentValidationStatus.pending,
							name: "Convention RTO",
							path: modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateRto.props.path.replace(
								":subscriptionId",
								subscriptionId ?? "",
							),
						},
					]),
			...(subscription?.productsSubscribed
				?.map((productSubscribed) => ({
					status: productSubscribed.documentsValidatedStatus ?? EDocumentValidationStatus.pending,
					name: productSubscribed.productSheet?.name ?? "",
					path: modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateDocuments.props.path
						.replace(":subscriptionId", subscriptionId ?? "")
						.replace(":productSubscribedId", productSubscribed.id),
				}))
				.sort((a, b) => a.name.localeCompare(b.name)) ?? []),
		];
	}, [subscription, subscriptionId, privateEquityOnly]);

	const checkValidationDocumentIsCompleted = useCallback((status: EDocumentValidationStatus) => {
		return status === EDocumentValidationStatus.validated;
	}, []);

	const isInDocumentValidationStep = useMemo(() => {
		return documentValidationSteps.some((step) => matchPath(step.path, location.pathname));
	}, [documentValidationSteps, location.pathname]);

	const updateCanSendSubscription = useCallback(() => {
		if (!productAggregate) return;
		const isValid = filteredSteps.every((step) => checkCategoryIsCompleted(step.category));
		setCanSendSubscription(isValid);
	}, [checkCategoryIsCompleted, productAggregate, filteredSteps]);

	const goToNextStep = useCallback(() => {
		// Get in the stepByCategory object the key that has the value of the currentStep
		const actualStepKey = filteredSteps.findIndex((step) => step.category === actualCategory) ?? 0;
		const nextStep = filteredSteps?.[actualStepKey + 1];

		if (!nextStep) return;
		return navigate(nextStep.path.replace(":subscriptionId", subscriptionId as string));
	}, [actualCategory, navigate, subscriptionId, filteredSteps]);

	const getInputsFromProductAggregate = useCallback(
		(
			inputList:
				| Record<EPatrimoineRevenusCorporationGroups, JSX.Element>
				| Record<EConnaissanceExperienceCorporationGroups, JSX.Element>
				| Record<ETestAdequationCorporationGroups, JSX.Element>
				| Record<EAutreCgpCorporationGroups, JSX.Element>
				| Record<EFinanceDurableCorporationGroups, JSX.Element>,
		) => {
			if (!productAggregate || !actualCategory) return;
			const inputs: JSX.Element[] = [];
			// Get the keys of the inputs, required in order to keep the order of the inputs
			const inputsKeys = Object.keys(inputList);
			inputsKeys.forEach((key) => {
				// Get the key of the inputList and check if it exists in the productAggregate, if so, push the input to the inputs array
				if (productAggregate[actualCategory]?.groups.find((question) => question === key)) inputs.push(inputList[key as keyof typeof inputList]);
			});
			return inputs;
		},
		[actualCategory, productAggregate],
	);

	const loadSubscription = useCallback(async () => {
		if (!subscriptionId) return navigate(modules.pages.Home.props.path);
		await subscriptionService.syncDraftWithLastProductSheet(subscriptionId);
		const subscription = await subscriptionService.getByUid(subscriptionId ?? "ddezddd");
		if (!subscription || !subscription.productsSubscribed || !subscription.productsSubscribed[0]?.corporationSubscriber) return navigate(modules.pages.Home.props.path);
		setSubscription(subscription);
		setProductAggregate(
			getCorporationProductsAggregate(subscription.productsSubscribed!.map((product) => product.productSheet!.product?.referenceName as TCorporationProductName)),
		);
		const clientKnowledge = await subscriptionService.getCorporationKnowledge(subscription.productsSubscribed[0]?.corporationSubscriber.corporationId!);
		const subscriptionWithKnowledge = _.mergeWith({ ...clientKnowledge }, subscription, (objValue: any, srcValue: any) => {
			if (srcValue === null) {
				return objValue; // Retourne la valeur originale, ignorant la valeur null
			}
		});
		setSubscriptionWithKnowledge(subscriptionWithKnowledge);
		setIsLoading(false);
	}, [navigate, subscriptionId]);

	const updateSubscription = useCallback(
		async (
			resource:
				| GenericPatrimoineRequestResourceCorporation
				| GenericConnaissanceExperienceRequestResourceCorporation
				| GenericTestAdequationRequestResourceCorporation
				| GenericFinanceDurableRequestResourceCorporation
				| GenericAutreCgpRequestResourceCorporation,
		) => {
			const proceed = async () => {
				await subscriptionService
					.updateCategoryCorporation(resource, subscriptionId as string, actualCategory as keyof GenericCorporationProduct)
					.then(async (_subscription) => {
						loadSubscription();
						setSubscriptionHasUnsavedChanges(false);
						setShouldTriggerDocumentResetWarning(false);
						setPendingSubmitCallback(null);

						// Désactiver la vérification des documents
						setCanEnableDocumentStep(false);
						await subscriptionService.resetSubscriptionDocumentsStatus(subscriptionId as string);
						if (currentSelectedPath !== "") {
							navigate(currentSelectedPath);
							setCurrentSelectedPath("");
							return;
						}
						goToNextStep();
					})
					.catch((err) => {
						setCurrentSelectedPath("");
						console.error(err);
					});
			};
			if (shouldTriggerDocumentResetWarning) {
				setPendingSubmitCallback(() => proceed);
				openResetWarningModal();
				return;
			}

			await proceed();
		},
		[
			subscriptionId,
			actualCategory,
			loadSubscription,
			currentSelectedPath,
			goToNextStep,
			navigate,
			shouldTriggerDocumentResetWarning,
			openResetWarningModal,
			setCanEnableDocumentStep,
		],
	);

	const updateConditionsSubscription = useCallback(
		async (resource: SubscriptionLightUpdateRequestResource) => {
			await subscriptionService
				.updateConditionsInformations(resource)
				.then(() => {
					loadSubscription();
				})
				.catch((err) => {
					console.error(err);
				});
		},
		[loadSubscription],
	);

	const sendSubcriptionToMo = useCallback(async () => {
		if (!subscriptionId) return;
		if (validateDocumentsStepEnabled) {
			navigate(modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateRto.props.path.replace(":subscriptionId", subscriptionId));
		} else {
			await subscriptionService.submitCorporation(subscriptionId);
			openValidatedModal();
		}
	}, [subscriptionId, navigate, openValidatedModal, validateDocumentsStepEnabled]);

	const onValidatedModalClose = useCallback(() => {
		closeValidatedModal();
		navigate(modules.pages.Subscriptions.props.pages.WaitingSignature.props.path);
	}, [closeValidatedModal, navigate]);

	const onCloseUnsavedChangeModal = useCallback(() => {
		closeUnsavedChangeModal();
		setCurrentSelectedPath("");
	}, [closeUnsavedChangeModal]);

	const continueWithoutSaving = useCallback(() => {
		closeUnsavedChangeModal();
		setSubscriptionHasUnsavedChanges(false);
		navigate(currentSelectedPath);
	}, [currentSelectedPath, navigate, closeUnsavedChangeModal]);

	const onModalConfirmUnsavedChange = useCallback(() => {
		closeUnsavedChangeModal();
		const event = new Event("submit", { bubbles: true });
		currentFormElement && currentFormElement.dispatchEvent(event);
	}, [closeUnsavedChangeModal, currentFormElement]);

	const onClickedLink = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, currentStepPath: string) => {
			if (!subscriptionHasUnsavedChanges) return;
			e.preventDefault();
			setCurrentSelectedPath(currentStepPath);
			openUnsavedChangeModal();
		},
		[subscriptionHasUnsavedChanges, openUnsavedChangeModal],
	);

	const onClickedValidationDocumentLink = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
			if (subscriptionHasUnsavedChanges) {
				e.preventDefault();
				setCurrentSelectedPath(targetPath);
				openUnsavedChangeModal();
			} else {
				e.preventDefault();
				navigate(targetPath);
			}
		},
		[subscriptionHasUnsavedChanges, navigate, openUnsavedChangeModal],
	);
	const setSubscriptionHasUnsavedChangesToTrue = useCallback(
		(e: React.ChangeEvent<HTMLFormElement>) => {
			setCurrentFormElement(e.currentTarget);
			setSubscriptionHasUnsavedChanges(true);

			const currentStepIsEditable = actualCategory !== "files" && actualCategory !== null;

			if (currentStepIsEditable && canSendSubscription && requiresElectronicSignature) {
				setShouldTriggerDocumentResetWarning(true);
			}

			setCanEnableDocumentStep(false);
		},
		[actualCategory, canSendSubscription, requiresElectronicSignature],
	);

	const setSubscriptionHasUnsavedChangesToFalse = useCallback(() => {
		setSubscriptionHasUnsavedChanges(false);
	}, []);

	const getTranslatedCategory = useCallback(() => {
		const rtoDocumentPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateRto.props.path.replace(
			":subscriptionId",
			subscriptionId ?? "",
		);

		if (matchPath(rtoDocumentPath, location.pathname)) {
			return I18n.asset.pages.subscriptions.validation_documents;
		}

		// Check if current path matches any product's document validation route
		const matchedProduct = subscription?.productsSubscribed?.find((product) => {
			const productPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateDocuments.props.path
				.replace(":subscriptionId", subscriptionId ?? "")
				.replace(":productSubscribedId", product.id);

			return matchPath(productPath, location.pathname);
		});

		if (matchedProduct) {
			return I18n.asset.pages.subscriptions.validation_documents;
		}

		// Fallback to actual subscription step
		return actualCategory ? I18n.asset.pages.subscriptions[actualCategory] : I18n.asset.pages.subscriptions.summary;
	}, [actualCategory, subscriptionId, location.pathname, subscription]);

	useEffect(() => {
		const corporationId = subscription?.productsSubscribed?.[0]?.corporationSubscriber?.corporationId;
		if (!corporationId) return;
		corporationService.getCorporationSheetByCorporationId(corporationId).then(setCorporation);
	}, [subscription]);

	useEffect(() => {
		const signDocumentsPath = modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.ValidateRto.props.path.replace(
			":subscriptionId",
			subscriptionId ?? "",
		);
		if (matchPath(location.pathname, signDocumentsPath) && !hasTutoModalBeenOpened) {
			openSignDocumentTutoModal();
			setHasTutoModalBeenOpened(true);
		}
	}, [location.pathname, subscriptionId, hasTutoModalBeenOpened, openSignDocumentTutoModal]);

	useEffect(() => {
		if (!subscriptionId) return;
		(async () => {
			const eligibleSignatureAutomation = await subscriptionService.eligibleSignatureAutomation(subscriptionId);
			setValidateDocumentsStepEnabled(eligibleSignatureAutomation);
		})();
	}, [subscriptionId]);

	useEffect(() => {
		// each time the subscription is updated, check if the subscription is valid and can be sent
		updateCanSendSubscription();
	}, [updateCanSendSubscription, subscription]);

	useEffect(() => {
		setActualCategory(filteredSteps.find((step) => matchPath(step.path, location.pathname))?.category ?? null);
		// If the location matches the path of the root without any step, go to the next step, which means go to the first step
		if (!productAggregate) return;
		const match = matchPath(modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.props.path, location.pathname);
		if (!!match) return goToNextStep();
	}, [goToNextStep, location.pathname, productAggregate, filteredSteps]);

	useEffect(() => {
		if (!subscriptionId) return navigate(modules.pages.Home.props.path);
		loadSubscription();
	}, [loadSubscription, navigate, subscriptionId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	return (
		<>
			{isLoading || !productAggregate || !subscription ? (
				<div>Chargement</div>
			) : (
				<I18n>
					<div className={classes["root"]}>
						<div className={classes["top-separator"]} />
						<main className={classes["body"]}>
							{subscriptionId && (
								<SubscriptionQuestionsStepper
									subscriptionId={subscriptionId}
									checkCategoryIsCompleted={checkCategoryIsCompleted}
									onClickedLink={onClickedLink}
									signDocumentStepEnabled={validateDocumentsStepEnabled}
									signDocumentStepActive={canSendSubscription && (subscription.hasAcceptedConditions ?? false)}
									steps={filteredSteps}
								/>
							)}
							<div className={classes["content-right"]}>
								<Typography typo={ITypo.DISPLAY_MEDIUM} className={classes["title"]}>
									{getTranslatedCategory().page_title}
								</Typography>
								<div className={classes["content"]}>
									<Outlet
										context={
											{
												getInputsFromProductAggregate,
												productAggregate,
												updateSubscription,
												subscription,
												goToNextStep,
												canSendSubscription,
												loadSubscription,
												sendSubcriptionToMo,
												setSubscriptionHasUnsavedChangesToTrue,
												setSubscriptionHasUnsavedChangesToFalse,
												currentSelectedPath,
												setCurrentSelectedPath,
												subscriptionWithKnowledge,
												setCanEnableDocumentStep,
												canEnableDocumentStep,
												documentValidationSteps,
												updateConditionsSubscription,
											} as ISubscriptionOutletContext
										}
									/>
								</div>
							</div>
						</main>
						{/* Produits souscrits : visible tant que la page actuelle est avant 'validate_rto' */}
						{!isInDocumentValidationStep && (
							<div className={classes["top-recap"]}>
								<Typography typo={ITypo.P_LARGE_BOLD} className={classes["title"]}>
									Produits souscrits
								</Typography>
								<div className={classes["separator"]}>&nbsp;</div>
								<div className={classes["subscribers-container"]}>
									<div className={classes["subscriber-text"]}>
										<Typography typo={ITypo.P_MEDIUM_BOLD}>Souscripteur :</Typography>
										<Button variant={EButtonVariant.TEXT} onClick={editCorporationModal.open}>
											{I18n.asset.common.modify}
										</Button>
									</div>
									<div className={classes["subscribers"]}>
										<Typography typo={ITypo.P_MEDIUM}>{subscriber?.raisonSociale}</Typography>
									</div>
								</div>
								<div className={classes["separator"]}>&nbsp;</div>
								<div className={classes["products"]}>
									{subscription?.productsSubscribed?.map((productSubscribed) => (
										<div className={classes["product"]} key={productSubscribed.id}>
											<div className={classes["product-header"]}>
												<Typography typo={ITypo.P_MEDIUM_BOLD}>{productSubscribed.productSheet!.name}</Typography>
												<Typography typo={ITypo.P_MEDIUM_BOLD}>
													{NumberUtils.formatNumberAddingSpaces(getProductSubscribedAmount(productSubscribed))}€{" "}
												</Typography>
											</div>
											<div className={classes["product-infos"]}>
												<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.enums.EPaymentMethod[productSubscribed.paymentMethod]}</Typography>
												<Typography typo={ITypo.P_MEDIUM}>{productSubscribed.quantity} parts</Typography>
											</div>
											<div className={classes["product-separator"]} />
										</div>
									))}
								</div>
								<InvestorPortalOrigin variant="detail" invitation={subscription.investorInvitation} />
							</div>
						)}
						{isInDocumentValidationStep && (
							<ValidationDocumentsStepperCorporation
								onClickedLink={onClickedValidationDocumentLink}
								checkValidationIsCompleted={checkValidationDocumentIsCompleted}
								steps={documentValidationSteps}
							/>
						)}
					</div>
				</I18n>
			)}
			<Modal isOpen={isOpenUnsavedChangeModal} onClose={onCloseUnsavedChangeModal} className={classes["modal-content"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.unsaved_modifications}</Typography>
				<div className={classes["button-container"]}>
					<Button onClick={continueWithoutSaving} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.pages.subscriptions.continue_unsave}
					</Button>
					<Button onClick={onModalConfirmUnsavedChange}>{I18n.asset.common.save}</Button>
				</div>
			</Modal>
			<Modal isOpen={isOpenValidatedModal}>
				<div className={classes["modal-content"]}>
					<Typography typo={ITypo.H1} className={classes["modal-title"]}>
						{I18n.asset.pages.subscriptions.upload_documents.modal.title}
					</Typography>
					<Typography typo={ITypo.P_MEDIUM} className={classes["modal-description"]}>
						{I18n.asset.pages.subscriptions.upload_documents.modal.description}
						<a href={`mailto:${I18n.asset.pages.subscriptions.upload_documents.modal.address}`} rel="noreferrer" target="_blank">
							{I18n.asset.pages.subscriptions.upload_documents.modal.address}
						</a>
					</Typography>
					{subscription?.productsSubscribed?.some((p) => p.subscriptionProductType !== ESubscriptionProductType.pleine_propriete) && (
						<div className={classes["demembrement-note"]}>{I18n.asset.pages.subscriptions.upload_documents.modal.demembrement_note}</div>
					)}
					<Button onClick={onValidatedModalClose} className={classes["modal-button"]}>
						{I18n.asset.common.close}
					</Button>
				</div>
			</Modal>
			<Modal isOpen={isOpenSignDocumentTutoModal} className={classes["modal-tuto-root"]} style={{ maxWidth: "570px", width: "100%" }}>
				<Typography typo={ITypo.H1}>{I18n.asset.pages.subscriptions.validation_documents.modal.title}</Typography>
				<div className={classes["modal-tuto-content"]}>
					<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.pages.subscriptions.validation_documents.modal.now}</Typography>
					<Typography typo={ITypo.P_MEDIUM} className={classes["underline"]}>
						{I18n.asset.pages.subscriptions.validation_documents.modal.next_step}
					</Typography>
					<div className={classes["modal-tuto-steps"]}>
						{!privateEquityOnly && <Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.validation_documents.modal.validate_convention}</Typography>}
						<Typography typo={ITypo.P_MEDIUM_BOLD}>
							{privateEquityOnly
								? I18n.asset.pages.subscriptions.validation_documents.modal.validate_documents_without_rto
								: I18n.asset.pages.subscriptions.validation_documents.modal.validate_documents}
						</Typography>
					</div>
					<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.pages.subscriptions.validation_documents.modal.report}</Typography>
					<Typography typo={ITypo.P_MEDIUM}>
						{I18n.asset.pages.subscriptions.validation_documents.modal.question}
						<br />
						<a href={`mailto:${I18n.asset.pages.subscriptions.validation_documents.modal.address}`} rel="noreferrer" target="_blank">
							{I18n.asset.pages.subscriptions.validation_documents.modal.address}
						</a>
					</Typography>
				</div>
				<Button onClick={closeSignDocumentTutoModal}>{I18n.asset.pages.subscriptions.validation_documents.modal.close}</Button>
			</Modal>
			<Modal isOpen={isOpenResetWarningModal} onClose={closeResetWarningModal} className={classes["modal-content"]} style={{ maxWidth: "570px", width: "100%" }}>
				<Typography typo={ITypo.H1} className={classes["modal-title"]} style={{ marginBottom: "8px" }}>
					{I18n.asset.pages.subscriptions.reset_documents_modal.title}
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["modal-description"]} style={{ marginBottom: "8px" }}>
					{I18n.asset.pages.subscriptions.reset_documents_modal.message}
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.reset_documents_modal.warning_text}</Typography>

				<div className={classes["modal-button"]}>
					<Button onClick={closeResetWarningModal} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
					<Button
						onClick={() => {
							closeResetWarningModal();
							setShouldTriggerDocumentResetWarning(false);

							if (pendingSubmitCallback) {
								pendingSubmitCallback();
							}
						}}>
						{I18n.asset.pages.subscriptions.reset_documents_modal.confirm_button}
					</Button>
				</div>
			</Modal>
			{corporation && subscription && (
				<EditCorporationModal
					title={I18n.asset.pages.client.sub_pages.personal_information.edit_corporation_modal.title}
					subtitle={I18n.asset.pages.client.sub_pages.personal_information.edit_corporation_modal.subtitle}
					isOpen={editCorporationModal.isOpen}
					onClose={editCorporationModal.close}
					onContinue={editCorporationModal.close}
					corporation={corporation}
					subscription={subscription}
					isFromSubscriptionTunnel
				/>
			)}
		</>
	);
}
